import { computed, inject, Injector, signal } from "@angular/core";
import { CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { AccApplicationDialogDataService } from "../acc-application-dialog-data.service";


export interface ApplicationInterviewFormValues {
	valuePropositionRating: number,
	sustainabilityRating: number,
	coachabilityRating: number,
	timeCommitmentRating: number,
	notes: string,
}

export class AccApplicationInteviewFormService {

	private readonly injector = inject(Injector)
	public readonly form = new CommonForm<ApplicationInterviewFormValues>(this.injector);
	public readonly accAppDialogDataService = inject(AccApplicationDialogDataService);

	public readonly accApplicationInterview = computed(() => this.accAppDialogDataService.dialogData()?.details.interview);

	public readonly disableSaveBtn = computed(() => {
		const accApplicationInterview = this.accApplicationInterview();
		if (!accApplicationInterview) return false;

		const valuePropositionRating = this.valueProposition().pendingValue() || this.valueProposition().actualValue();
		const sustainabilityRating = this.profitabilitySustainability().pendingValue() || this.profitabilitySustainability().actualValue();
		const coachabilityRating = this.coachability().pendingValue() || this.coachability().actualValue();
		const timeCommitmentRating = this.timeCommitment().pendingValue() || this.timeCommitment().actualValue();
		const notes = this.notes().pendingValue() || this.notes().actualValue();

		const flag = valuePropositionRating == accApplicationInterview.valuePropositionRating &&
			sustainabilityRating == accApplicationInterview.sustainabilityRating &&
			coachabilityRating == accApplicationInterview.coachabilityRating &&
			timeCommitmentRating == accApplicationInterview.timeCommitmentRating &&
			notes == accApplicationInterview.notes;

		return flag;
	});

	private readonly valuePropositionOpts = computed(() => [
		{ text: 'Unresearched value proposition', value: 1 },
		{ text: 'Minimally researched value proposition', value: 2 },
		{ text: 'Well researched value proposition', value: 3 },
		{ text: 'Well researched and well presented value proposition', value: 4 }
	]);

	readonly valueProposition = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Value Proposition', options: this.valuePropositionOpts(), isRequired: true, isSpanish: false,
			isEnabled: computed(() => !this.accAppDialogDataService.isReadonly()),
		}),
		actualValue: computed(() => this.accApplicationInterview()?.valuePropositionRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'valuePropositionRating',
		form: this.form,
	}));

	private readonly profitabilitySustainabilityOpts = computed(() => [
		{ text: 'Profitability not considered', value: 1 },
		{ text: 'Profitability minimally considered', value: 2 },
		{ text: 'Profitability well considered', value: 3 },
		{ text: 'Profitability considered and demonstrated', value: 4 }
	]);

	readonly profitabilitySustainability = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Profitability/Sustainability', options: this.profitabilitySustainabilityOpts(), isRequired: true, isSpanish: false,
			isEnabled: computed(() => !this.accAppDialogDataService.isReadonly()),
		}),
		actualValue: computed(() => this.accApplicationInterview()?.sustainabilityRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'sustainabilityRating',
		form: this.form,
	}));


	private readonly coachabilityOpts = computed(() => [
		{ text: 'Unwilling to adapt to new information', value: 1 },
		{ text: 'Uncomfortable adapting to new information', value: 2 },
		{ text: 'Somewhat comfortable adapting to new information', value: 3 },
		{ text: 'Extremely comfortable adapting to new information', value: 4 }
	]);

	readonly coachability = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Coachability', options: this.coachabilityOpts(), isRequired: true, isSpanish: false,
			isEnabled: computed(() => !this.accAppDialogDataService.isReadonly()),
		}),
		actualValue: computed(() => this.accApplicationInterview()?.coachabilityRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'coachabilityRating',
		form: this.form,
	}));


	private readonly timeCommitmentOpts = computed(() => [
		{ text: 'Very unlikely to have time/passion/commitment', value: 1 },
		{ text: 'Somewhat unlikely to have time/passion/commitment', value: 2 },
		{ text: 'Likely to have time/passion/commitment', value: 3 },
		{ text: 'Very likely to have time/passion/commitment', value: 4 }
	]);

	readonly timeCommitment = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Time/Passion/Commitment', options: this.timeCommitmentOpts(), isRequired: true, isSpanish: false,
			isEnabled: computed(() => !this.accAppDialogDataService.isReadonly()),
		}),
		actualValue: computed(() => this.accApplicationInterview()?.timeCommitmentRating || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'timeCommitmentRating',
		form: this.form,
	}));


	readonly notes = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Notes', min: 1, max: 2000, fixCase: 'sentence-case', isRequired: true, multiLine: true, isSpanish: false,
			isEnabled: computed(() => !this.accAppDialogDataService.isReadonly()),
		}),
		actualValue: computed(() => this.accApplicationInterview()?.notes || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'notes',
		form: this.form,
	}));


	public revertForm() {
		this.valueProposition().pendingValue.set(this.accApplicationInterview()?.valuePropositionRating || 0);
		this.profitabilitySustainability().pendingValue.set(this.accApplicationInterview()?.sustainabilityRating || 0);
		this.coachability().pendingValue.set(this.accApplicationInterview()?.coachabilityRating || 0);
		this.timeCommitment().pendingValue.set(this.accApplicationInterview()?.timeCommitmentRating || 0);
		this.notes().pendingValue.set(this.accApplicationInterview()?.notes || '');
	}



}